import React, { useState, useCallback, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { wrap } from "popmotion";
import { debounce } from "lodash";
import { useDimensions } from "hooks/useDimension";
import { images, imagesMobile } from "./image-data";

import * as S from "./styles";

const variants = {
  enter: (direction: number) => {
    return {
      y: direction > 0 ? "100vh" : "-100vh",
      scale: 1,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    y: 0,
    scale: 1,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      y: direction < 0 ? "100vh" : "-100vh",
      opacity: 0,
    };
  },
};

export const Home: React.FC = () => {
  const { width } = useDimensions();

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [[page, direction], setPage] = useState([0, 0]);
  const [[pageMobile, directionMobile], setPageMobile] = useState([0, 0]);

  const imageIndex = wrap(0, images.length, page);

  const imageIndexMobile = wrap(0, imagesMobile.length, pageMobile);

  const paginate = useCallback(
    (newDirection: number) => {
      setPage([page + newDirection, newDirection]);
    },
    [page]
  );

  const paginateMobile = useCallback(
    (newDirection: number) => {
      setPageMobile([pageMobile + newDirection, newDirection]);
    },
    [pageMobile]
  );

  const checkWidth = useCallback((): void => {
    setIsMobile(width <= 1000);
  }, [width]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollSlider = useCallback(
    debounce((event) => {
      if (width > 1000) {
        if (event.deltaY >= 0) {
          paginate(1);
        }
        if (event.deltaY <= -0) {
          paginate(-1);
        }
      }
    }, 100),
    [paginate]
  );

  const renderAnimateSlide = useCallback(() => {
    if (isMobile) {
      return (
        <AnimatePresence initial={false} custom={directionMobile}>
          <S.Slide
            key={pageMobile}
            custom={directionMobile}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              y: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
          >
            {imagesMobile[imageIndexMobile].component}
          </S.Slide>
        </AnimatePresence>
      );
    }

    return (
      <AnimatePresence initial={false} custom={direction}>
        <S.Slide
          key={page}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            y: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
        >
          {images[imageIndex].component}
        </S.Slide>
      </AnimatePresence>
    );
  }, [
    direction,
    directionMobile,
    imageIndex,
    imageIndexMobile,
    isMobile,
    page,
    pageMobile,
  ]);

  useEffect(() => {
    window.addEventListener("wheel", scrollSlider);

    return () => {
      window.removeEventListener("wheel", scrollSlider);
    };
  }, [scrollSlider]);

  useEffect(() => {
    checkWidth();
  }, [checkWidth]);

  return (
    <S.Container>
      {renderAnimateSlide()}
      <S.ScrollInfoWrapper>
        <S.ScrollInfo>
          <S.IconMouse />
          ROLE PARA AVANÇAR
        </S.ScrollInfo>
      </S.ScrollInfoWrapper>
      <S.ButtonPrevMobile
        onClick={() => paginateMobile(-1)}
        slide={imageIndexMobile + 1}
      >
        <S.IconLeft />
      </S.ButtonPrevMobile>
      <S.ButtonNextMobile
        onClick={() => paginateMobile(1)}
        slide={imageIndexMobile + 1}
      >
        Próximo
      </S.ButtonNextMobile>
    </S.Container>
  );
};
